'use client';

import React from 'react';
import { motion } from 'framer-motion';
import { Syringe, ArrowRight, CalendarCheck } from 'lucide-react';
import { useVaccines } from '@/hooks/useVaccines';
import { useTheme } from '@/providers/ThemeProvider';

interface VaccineBannerProps {
  /** Navigate to the booking flow (e.g. router.push('/vaccines')). */
  onBook: () => void;
  /** Optional copy overrides — defaults derive from the pharmacy name. */
  title?: string;
  subtitle?: string;
  ctaLabel?: string;
  /** Cap how many vaccine names are listed inline (default 4). */
  maxNames?: number;
  className?: string;
}

// Home-screen promo banner: "We offer X, Y, Z — schedule your appointment".
// Fully theme-driven (pharmacy name + brand colors); hides itself when the store
// has no active vaccines so it's safe to drop onto any page.
export function VaccineBanner({
  onBook,
  title,
  subtitle,
  ctaLabel = 'Schedule appointment',
  maxNames = 4,
  className = '',
}: VaccineBannerProps) {
  const { vaccines, isLoading } = useVaccines();
  const { config } = useTheme();

  if (isLoading || vaccines.length === 0) return null;

  const names = vaccines.map((v) => v.name);
  const shown = names.slice(0, maxNames);
  const extra = names.length - shown.length;

  const heading = title ?? `Vaccinations at ${config.storeName}`;
  const sub =
    subtitle ??
    `Now offering ${shown.join(', ')}${extra > 0 ? ` +${extra} more` : ''}. Book in minutes.`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className={`relative overflow-hidden rounded-2xl bg-gradient-to-br from-hprimary-600 to-hprimary-800 p-5 text-white shadow-lg sm:p-7 ${className}`}
    >
      {/* Decorative blobs */}
      <div className="pointer-events-none absolute -right-8 -top-8 h-40 w-40 rounded-full bg-white/10 blur-2xl" />
      <div className="pointer-events-none absolute -bottom-10 right-16 h-32 w-32 rounded-full bg-white/10 blur-2xl" />

      <div className="relative flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white/15 backdrop-blur-sm">
            <Syringe className="h-6 w-6" />
          </div>
          <div className="min-w-0">
            <h3 className="text-lg font-bold leading-tight sm:text-xl">{heading}</h3>
            <p className="mt-1 text-sm text-white/85">{sub}</p>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {shown.map((n) => (
                <span key={n} className="rounded-full bg-white/15 px-2.5 py-1 text-xs font-medium backdrop-blur-sm">
                  {n}
                </span>
              ))}
              {extra > 0 && (
                <span className="rounded-full bg-white/10 px-2.5 py-1 text-xs font-medium">+{extra} more</span>
              )}
            </div>
          </div>
        </div>

        <motion.button
          type="button"
          onClick={onBook}
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          className="group inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-white px-5 py-3 text-sm font-semibold text-hprimary-700 shadow-md transition-shadow hover:shadow-lg"
        >
          <CalendarCheck className="h-4 w-4" />
          {ctaLabel}
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
        </motion.button>
      </div>
    </motion.div>
  );
}
